
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher';
import { supabase } from '../lib/supabase';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [logo, setLogo] = useState({
    url: 'https://static.readdy.ai/image/375f8353da3743c784fbc166ae5f72ce/e402424e5980e1f9422fd5129d8d193f.png',
    alt_ar: 'مكتب المحامية غدير سالم أشكناني',
    alt_en: 'Ghadeer Law Office',
  });
  const [navLinks, setNavLinks] = useState<Array<{
    path: string;
    label_ar: string;
    label_en: string;
  }>>([]);
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const isRTL = i18n.language === 'ar';

  // -------------------------------------------------
  // Scroll handling
  // -------------------------------------------------
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // -------------------------------------------------
  // Load logo & navigation settings whenever language changes
  // -------------------------------------------------
  useEffect(() => {
    loadNavbarSettings();
  }, [i18n.language]);

  const loadNavbarSettings = async () => {
    try {
      const [logoRes, navRes] = await Promise.all([
        supabase
          .from('site_settings')
          .select('*')
          .eq('setting_key', 'site_logo')
          .maybeSingle(),
        supabase
          .from('site_settings')
          .select('*')
          .eq('setting_key', 'navbar_settings')
          .maybeSingle(),
      ]);

      // ---- Logo ----
      if (logoRes.data?.setting_value) {
        // Expecting setting_value to contain { url, alt_ar, alt_en }
        setLogo(logoRes.data.setting_value);
      }

      // ---- Navigation links ----
      if (navRes.data?.setting_value?.links) {
        setNavLinks(navRes.data.setting_value.links);
      } else {
        // Fallback default links
        setNavLinks([
          { path: '/', label_ar: 'الرئيسية', label_en: 'Home' },
          { path: '/about', label_ar: 'من نحن', label_en: 'About' },
          { path: '/services', label_ar: 'الخدمات', label_en: 'Services' },
          { path: '/team', label_ar: 'الفريق', label_en: 'Team' },
          { path: '/faq', label_ar: 'الأسئلة الشائعة', label_en: 'FAQ' },
          { path: '/blog', label_ar: 'المدونة', label_en: 'Blog' },
          { path: '/contact', label_ar: 'اتصل بنا', label_en: 'Contact' },
        ]);
      }
    } catch (error) {
      console.error('Error loading navbar settings:', error);
      // Graceful fallback on error
      setNavLinks([
        { path: '/', label_ar: 'الرئيسية', label_en: 'Home' },
        { path: '/about', label_ar: 'من نحن', label_en: 'About' },
        { path: '/services', label_ar: 'الخدمات', label_en: 'Services' },
        { path: '/team', label_ar: 'الفريق', label_en: 'Team' },
        { path: '/faq', label_ar: 'الأسئلة الشائعة', label_en: 'FAQ' },
        { path: '/blog', label_ar: 'المدونة', label_en: 'Blog' },
        { path: '/contact', label_ar: 'اتصل بنا', label_en: 'Contact' },
      ]);
    }
  };

  return (
    <nav
      className={`
        fixed top-0 left-0 right-0 z-50 transition-all duration-500 animate-slide-down
        font-cairo bg-white border-b border-gray-100
        ${isScrolled ? 'shadow-lg shadow-black/5' : 'shadow-sm'}
      `}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group cursor-pointer">
            <img
              src={logo.url}
              alt={isRTL ? logo.alt_ar : logo.alt_en}
              className="h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`
                  relative font-medium transition-all duration-300 cursor-pointer py-2 px-3 rounded-lg text-sm
                  ${location.pathname === link.path
                    ? 'text-white bg-primary'
                    : 'text-gray-700 hover:text-white hover:bg-primary'}
                `}
              >
                {isRTL ? link.label_ar : link.label_en}
              </Link>
            ))}
            <LanguageSwitcher />
            <Link to="/booking">
              <button className="relative group px-6 py-2.5 bg-primary hover:bg-primary-dark text-white rounded-lg font-semibold overflow-hidden whitespace-nowrap cursor-pointer transition-all duration-300 hover:shadow-lg hover:shadow-primary/30 hover:scale-105">
                <span className="relative z-10">{t('hero.cta')}</span>
              </button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-gray-700"
          >
            <i
              className={`
                text-2xl transition-transform duration-300
                ${isMobileMenuOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'}
              `}
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 animate-fade-in shadow-xl">
          <div className="container mx-auto px-4 py-6 space-y-4">
            {navLinks.map((link, index) => (
              <div
                key={link.path}
                className="animate-slide-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <Link
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`
                    block py-3 px-4 rounded-xl font-medium transition-all duration-300 cursor-pointer
                    ${location.pathname === link.path
                      ? 'text-white bg-primary'
                      : 'text-gray-700 hover:text-white hover:bg-primary'}
                  `}
                >
                  {isRTL ? link.label_ar : link.label_en}
                </Link>
              </div>
            ))}

            <div className="pt-4 border-t border-gray-100 space-y-4">
              <LanguageSwitcher />
              <Link to="/booking" onClick={() => setIsMobileMenuOpen(false)}>
                <button className="w-full px-6 py-3 bg-primary hover:bg-primary-dark text-white rounded-xl font-semibold whitespace-nowrap cursor-pointer transition-all">
                  {t('hero.cta')}
                </button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
